import { Phone, MapPin, Mail, Globe, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { Link } from "react-router-dom";

const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.372-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.075-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 005.71 1.447h.006c6.582 0 11.945-5.336 11.948-11.896 0-3.176-1.24-6.165-3.495-8.411zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.976 1.005-3.638-.235-.374a9.86 9.86 0 01-1.511-5.26c.002-5.45 4.455-9.884 9.933-9.884 2.652 0 5.145 1.032 7.021 2.906a9.825 9.825 0 012.909 6.99c-.003 5.45-4.456 9.886-9.985 9.886z"/>
  </svg>
);

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/languagenestoffcial",
    Icon: Facebook,
    hover: "hover:bg-[#1877F2] hover:border-[#1877F2]",
    testId: "footer-social-facebook",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/languagenest_india?igsh=N2xmYnIxc2xzdTY1",
    Icon: Instagram,
    hover: "hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:border-transparent",
    testId: "footer-social-instagram",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/languagenestofficial/?viewAsMember=true",
    Icon: Linkedin,
    hover: "hover:bg-[#0A66C2] hover:border-[#0A66C2]",
    testId: "footer-social-linkedin",
  },
  {
    label: "YouTube",
    href: "https://youtube.com/@languagenestindia?si=gMrpWQZTPNWFDM2d",
    Icon: Youtube,
    hover: "hover:bg-[#FF0000] hover:border-[#FF0000]",
    testId: "footer-social-youtube",
  },
  {
    label: "WhatsApp Channel",
    href: "https://whatsapp.com/channel/0029Va8uxgDCnA7jbKD4Jv3K",
    Icon: WhatsAppIcon,
    hover: "hover:bg-[#25D366] hover:border-[#25D366]",
    testId: "footer-social-whatsapp",
  },
];

export const Footer = () => {
  return (
    <footer id="contact" className="mt-24 bg-brand-navy text-white grain relative overflow-hidden">
      <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-brand-red/20 blur-3xl" />
      <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-brand-gold/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Link to="/" data-testid="footer-logo-link" className="inline-flex items-center gap-3 mb-4">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_vibrant-language-hub/artifacts/8vmii199_Outlined_Logo-Photoroom.webp"
              alt="Language Nest India"
              className="h-56 w-auto object-contain bg-transparent"
              data-testid="footer-logo-img"
            />
          </Link>
          <p className="text-white/70 max-w-md leading-relaxed">
            Delhi&#39;s trusted institute for IELTS, PTE, Spoken English, Personality Development,
            Interview Training and Study Abroad guidance. Where students become global citizens.
          </p>
        </div>

        <div>
          <h4 className="font-heading font-bold text-brand-gold mb-4 text-sm uppercase tracking-widest">Reach us</h4>
          <ul className="space-y-3 text-white/80 text-sm">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-1 shrink-0 text-brand-red-light" />
              <a
                href="https://www.google.com/maps/search/?api=1&query=A-30,+2nd+floor,+Nanda+Rd,+Adarsh+Nagar,+Delhi,+110033,+India"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address"
                className="hover:text-brand-gold hover:underline underline-offset-4 transition-colors"
              >
                A-30, 2nd floor, Nanda Rd, Adarsh Nagar, Delhi, 110033, India
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 shrink-0 text-brand-red-light" />
              <span>
                <a href="tel:+919382166788" data-testid="footer-phone" className="hover:text-brand-gold transition-colors">
                  +91 93821 66788
                </a>
                <span className="text-white/60">, </span>
                <a href="tel:+919560263853" data-testid="footer-phone-2" className="hover:text-brand-gold transition-colors">
                  +91 95602 63853
                </a>
              </span>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 shrink-0 text-brand-red-light" />
              <a href="mailto:study@languagenestindia.com" className="hover:text-brand-gold transition-colors">
                study@languagenestindia.com
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Globe className="w-4 h-4 shrink-0 text-brand-red-light" />
              <a href="https://languagenestindia.com" target="_blank" rel="noreferrer" className="hover:text-brand-gold transition-colors">
                languagenestindia.com
              </a>
            </li>
          </ul>

          <div className="mt-8" data-testid="footer-socials">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-gold mb-4">Follow us</div>
            <div className="flex items-center gap-3 flex-wrap">
              {SOCIAL_LINKS.map(({ label, href, Icon, hover, testId }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  data-testid={testId}
                  className={`group inline-flex items-center justify-center w-14 h-14 rounded-full border border-white/15 bg-white/5 text-white transition-all duration-300 hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-8px_rgba(0,0,0,0.55)] ${hover}`}
                >
                  <Icon className="w-7 h-7" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-heading font-bold text-brand-gold mb-4 text-sm uppercase tracking-widest">Explore</h4>
          <ul className="space-y-2 text-white/80 text-sm">
            <li><a href="/coaching" className="hover:text-brand-gold transition-colors">IELTS & PTE</a></li>
            <li><a href="/coaching" className="hover:text-brand-gold transition-colors">Spoken English</a></li>
            <li><a href="/coaching" className="hover:text-brand-gold transition-colors">Personality Development</a></li>
            <li><a href="/immigration" className="hover:text-brand-gold transition-colors">Study Abroad</a></li>
            <li><a href="/immigration" className="hover:text-brand-gold transition-colors">Immigration Coaching</a></li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <span>© {new Date().getFullYear()} Language Nest India. All rights reserved.</span>
          <span>Crafted with care in Adarsh Nagar, Delhi.</span>
        </div>
      </div>
    </footer>
  );
};
